"use client";

import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { Controller, useForm } from "react-hook-form";

import {
  type AddQuoteResult,
  type CatQuote,
  type PolicyHolder,
  ProductType,
  type WebShopPublicUser,
} from "@/api_client";
import { CatBreedAutoComplete } from "@/components/shared/catBreedAutoComplete/CatBreedAutoComplete";
import { ComposedInputField } from "@/components/shared/composedInputField/ComposedInputField";
import { ProductContainer } from "@/components/shared/productContainer/ProductContainer";
import { SsnInput } from "@/components/shared/ssnInput/SsnInput";
import { useEntry } from "@/hooks/useEntry";
import type { CatEntryContent } from "@/services/cms/contentTypes/entry/CatEntryContent";
import { useEntryContent } from "@/services/cms/tanstackQuery/hooks/useEntryContent";
import {
  type CatEntryFormType,
  CatEntryValidationSchema,
} from "@/validators/products/cat/CatEntryValidator";

export type EntryProps = {
  externalUser?: WebShopPublicUser;
  externalPolicyHolder?: PolicyHolder | null;
  externalContent?: CatEntryContent;
  externalIsLoading?: boolean;
  externalSubmitHandler?: (quote: CatQuote) => Promise<AddQuoteResult>;
};

export const CatEntryForm = ({
  externalUser,
  externalSubmitHandler,
  externalIsLoading,
  externalContent,
  externalPolicyHolder,
}: EntryProps): React.JSX.Element => {
  const { currentUser, currentPolicyHolder, isLoading, submitHandler } =
    useEntry<CatQuote, CatEntryContent>({
      externalUser,
      externalSubmitHandler,
      externalIsLoading,
      externalContent,
      externalPolicyHolder,
    });
  const { data: catEntryPage } =
    useEntryContent<typeof ProductType.CAT>(externalContent);

  const defaultValues: CatQuote = {
    productType: ProductType.CAT,
    policyHolderSsn:
      currentPolicyHolder?.prefilledSsn ?? currentUser?.ssn ?? "",
    catCovered: {
      catName: "",
      catBreed: "",
    },
  };
  const { handleSubmit, control, formState } = useForm<CatEntryFormType>({
    defaultValues,
    resolver: yupResolver(CatEntryValidationSchema(catEntryPage)),
    disabled: isLoading,
  });
  const { errors } = formState;

  return (
    <ProductContainer>
      <ProductContainer.Entry>
        <ProductContainer.Form
          noValidate
          onSubmit={handleSubmit(submitHandler)}
        >
          <Controller
            control={control}
            name="catCovered.catName"
            render={({ field }) => (
              <ComposedInputField
                id={field.name}
                placeholder={catEntryPage?.catName?.Placeholder ?? undefined}
                data-testid="cat-name-input"
                errorMessage={errors?.catCovered?.catName?.message}
                popoverMessage={catEntryPage?.catName?.Popover}
                {...field}
              >
                {catEntryPage?.catName?.Label}
              </ComposedInputField>
            )}
          />
          <Controller
            control={control}
            name="catCovered.catBreed"
            render={({ field }) => (
              <CatBreedAutoComplete
                id="destinationCountry"
                dataTestid="cat-breed-input"
                errorMessage={errors.catCovered?.catBreed?.message}
                placeholder={catEntryPage?.catBreed?.Placeholder ?? undefined}
                popoverMessage={catEntryPage?.catBreed?.Popover}
                {...field}
              >
                {catEntryPage?.catBreed?.Label}
              </CatBreedAutoComplete>
            )}
          />
          <Controller
            control={control}
            name="policyHolderSsn"
            render={({ field }) => (
              <SsnInput
                id={field.name}
                data-testid="policy-holder-ssn-input"
                placeholder={
                  catEntryPage?.policyHolderSsn?.Placeholder ?? undefined
                }
                disabled={
                  currentPolicyHolder?.ssnLocked ||
                  currentUser?.isAuthenticated ||
                  isLoading
                }
                popoverMessage={catEntryPage?.policyHolderSsn?.Popover}
                errorMessage={errors?.policyHolderSsn?.message}
                {...field}
              >
                {catEntryPage?.policyHolderSsn?.Label}
              </SsnInput>
            )}
          />
          <ProductContainer.CallToAction
            htmlType="submit"
            data-testid="cat-entry-submit-button"
            loading={isLoading}
          >
            {catEntryPage?.submitButton?.Label}
          </ProductContainer.CallToAction>
        </ProductContainer.Form>
      </ProductContainer.Entry>
    </ProductContainer>
  );
};
